import { AHIcon, AHText, Box, Dialog, TouchableBox, useTheme } from "arkh";

import { graphql, useFragment } from "react-relay";
import { TodoEditDeadlinePopover_todo$key } from "./__generated__/TodoEditDeadlinePopover_todo.graphql";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { useCallback, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";

import { Platform } from "react-native";

import DateTimePicker, {
  DateTimePickerAndroid,
} from "@react-native-community/datetimepicker";

import { useTodoEditSetExpireAt } from "../todoMutationHooks/useTodoEditSetExpireAt";
import { WebDateSelector } from "@/src/components/mobile/WebDateSelector/WebDateSelector";
import { useTimeZone } from "@/src/OS/Timezone";
import { getActiveSystemLocale } from "@/src/OS/getActiveLocale";
import { FormattedMessage } from "react-intl";

export function TodoEditDeadlinePopoverComponent(props: {
  visible: boolean;
  expireAt: Date | null;
  setExpireAt: (date: Date | null) => void;
  onClose: () => void;
}) {
  const intl_tokens = useIntlTokens();

  const setDate = useCallback(
    (date: Date | null) => {
      props.setExpireAt(date);
    },
    [props]
  );
  const [autoFocus, setAutoFocus] = useState(false);
  const date = useMemo(() => props.expireAt, [props.expireAt]);
  const datePickerRef = useRef<DatePickerRef>(null);
  return (
    <Dialog
      visible={props.visible}
      onClose={() => {
        props.onClose();
      }}
      title={intl_tokens.Deadline}
      minHeight={350}
    >
      <DP
        date={date}
        setDate={setDate}
        onCancel={props.onClose}
        ref={datePickerRef}
        autoFocus={autoFocus}
      />

      <Suggestions
        selectedDate={date}
        onSuggestionPress={(d) => {
          if (d) {
            setDate(d.toJSDate());
            setAutoFocus(false);
          } else {
            setDate(DateTime.now().plus({ hour: 1 }).toJSDate());
            setAutoFocus(true);
          }
        }}
      />
    </Dialog>
  );
}

function AndroidTimeButton(props: {
  children: React.ReactNode;
  onPress: () => void;
}) {
  return (
    <TouchableBox
      onPress={() => {
        props.onPress();
      }}
      spaceHorizontal="small"
      spaceVertical="small"
      color="background"
      cornerRadius="medium"
    >
      <AHText numberOfLines={1} type="list-item-title" color="text-primary">
        {props.children}
      </AHText>
    </TouchableBox>
  );
}
function DPAndroid(props: {
  date: Date;
  setDate: (date: Date | null) => void;
  onCancel: () => void;
}) {
  const intl_tokens = useIntlTokens();
  const theme = useTheme();

  const now = useMemo(() => new Date(), []);
  return (
    <Box style={{ flexDirection: "row" }} gap="small" spaceVertical="small">
      <AndroidTimeButton
        onPress={() => {
          DateTimePickerAndroid.open({
            value: props.date,
            mode: "date",
            display: "calendar",
            minimumDate: now,
            positiveButton: {
              label: intl_tokens.Done,
              textColor: theme.primary,
            },
            negativeButton: {
              label: intl_tokens.Cancel,
              textColor: theme["text-secondary"],
            },
            onChange: (event, selectedDate) => {
              if (selectedDate && event.type === "set") {
                props.setDate(selectedDate);
              }
            },
          });
        }}
      >
        {props.date
          ? DateTime.fromJSDate(props.date).toLocaleString({
              ...DateTime.DATE_MED_WITH_WEEKDAY,
              weekday: "long",
              month: "short",
              year: undefined,
            })
          : null}
      </AndroidTimeButton>

      <AndroidTimeButton
        onPress={() => {
          DateTimePickerAndroid.open({
            value: props.date,
            mode: "time",
            is24Hour: true,
            display: "spinner",
            minuteInterval: 10,
            minimumDate: now,
            positiveButton: {
              label: intl_tokens.Done,
              textColor: theme.primary,
            },
            negativeButton: {
              label: intl_tokens.Cancel,
              textColor: theme["text-secondary"],
            },
            onChange: (event, selectedDate) => {
              if (selectedDate && event.type === "set") {
                props.setDate(selectedDate);
              }
            },
          });
        }}
      >
        {props.date
          ? DateTime.fromJSDate(props.date).toLocaleString(
              DateTime.TIME_24_SIMPLE
            )
          : null}
      </AndroidTimeButton>
    </Box>
  );
}
function ClearButton(props: { onPress: () => void }) {
  const intl_tokens = useIntlTokens();
  return (
    <Box style={{ alignItems: "center", flexDirection: "row" }}>
      <TouchableBox
        onPress={() => {
          props.onPress();
        }}
        color="negative"
        cornerRadius="large"
        spaceHorizontal="small"
        spaceVertical="small"
        style={{ flexDirection: "row", alignItems: "center" }}
      >
        <AHText type="list-item-title" color="text-inverted">
          {intl_tokens.Clear}
        </AHText>
      </TouchableBox>
    </Box>
  );
}
function Suggestions(props: {
  onSuggestionPress: (date: DateTime | null) => void;

  selectedDate: Date | null;
}) {
  const date = DateTime.now();
  const showCustomDate = !props.selectedDate;
  const selectedDate = props.selectedDate
    ? DateTime.fromJSDate(props.selectedDate)
    : null;
  function getBeforeWeekendDate(start: DateTime) {
    let date = start;
    while (!date.isWeekend) {
      date = date.plus({ days: 1 });
    }
    return date.minus({ days: 1 }).set({ hour: 15 }).startOf("hour");
  }
  const today15 = date.set({ hour: 15 }).startOf("hour");
  return (
    <Box spaceHorizontal="medium" spaceBottom="medium">
      <AHText type="subtitle" spaceVertical="small" color="text-secondary">
        <FormattedMessage
          defaultMessage="Set"
          description="TodoEditDeadlinePopover Suggestions Title"
        />
      </AHText>
      {today15.diff(date).as("hours") > 2 ? (
        <SuggestionItem
          title={
            <FormattedMessage
              defaultMessage="Today 15:00"
              description="TodoEditDeadlinePopover Suggestion"
            />
          }
          date={date.set({ hour: 15 }).startOf("hour")}
          selectedDate={selectedDate}
          onSuggestionPress={props.onSuggestionPress}
        />
      ) : (
        <SuggestionItem
          title={
            <FormattedMessage
              defaultMessage="Today 22:00"
              description="TodoEditDeadlinePopover Suggestion"
            />
          }
          date={date.set({ hour: 22 }).startOf("hour")}
          selectedDate={selectedDate}
          onSuggestionPress={props.onSuggestionPress}
        />
      )}
      <SuggestionItem
        title={
          <FormattedMessage
            defaultMessage="Tomorrow 15:00"
            description="TodoEditDeadlinePopover Suggestion"
          />
        }
        date={date.plus({ day: 1 }).set({ hour: 15 }).startOf("hour")}
        selectedDate={selectedDate}
        onSuggestionPress={props.onSuggestionPress}
      />
      {date.isWeekend ? null : (
        <SuggestionItem
          title={
            <FormattedMessage
              defaultMessage="Before Weekend"
              description="TodoEditDeadlinePopover Suggestion"
            />
          }
          date={getBeforeWeekendDate(date)}
          selectedDate={selectedDate}
          onSuggestionPress={props.onSuggestionPress}
        />
      )}
      <SuggestionItem
        title={
          <FormattedMessage
            defaultMessage="This Week"
            description="TodoEditDeadlinePopover Suggestion"
          />
        }
        date={date.endOf("week")}
        selectedDate={selectedDate}
        onSuggestionPress={props.onSuggestionPress}
      />
      <SuggestionItem
        title={
          <FormattedMessage
            defaultMessage="Next Week"
            description="TodoEditDeadlinePopover Suggestion"
          />
        }
        date={date.endOf("week").plus({ seconds: 1 }).endOf("week")}
        selectedDate={selectedDate}
        onSuggestionPress={props.onSuggestionPress}
      />
      {showCustomDate ? (
        <SuggestionItem
          selectedDate={null}
          title={
            <FormattedMessage
              defaultMessage="Custom"
              description="TodoEditDeadlinePopover Suggestion"
            />
          }
          onSuggestionPress={props.onSuggestionPress}
        />
      ) : null}
    </Box>
  );
}
function SuggestionItem(props: {
  title: React.ReactNode;
  date?: DateTime;
  onSuggestionPress: (date: DateTime | null) => void;
  selectedDate: DateTime | null;
}) {
  const isSelected =
    props.date &&
    props.date?.toUnixInteger() === props.selectedDate?.toUnixInteger();
  return (
    <TouchableBox
      onPress={() => props.onSuggestionPress(props.date ?? null)}
      style={{ flexDirection: "row", alignItems: "center" }}
      spaceVertical="small"
      gap="small"
    >
      <AHIcon
        type="deadline"
        size="small"
        color={isSelected ? "negative" : "text-secondary"}
      />
      <AHText type="list-item-title" color="text-primary">
        {props.title}
      </AHText>
      {props.date ? (
        <AHText type="list-item-title" color="text-washed">
          {props.date.toLocaleString({
            ...DateTime.DATETIME_MED_WITH_WEEKDAY,
            year: undefined,
          })}
        </AHText>
      ) : null}
    </TouchableBox>
  );
}

type DatePickerRef = {
  focus: () => void;
};
function DP(props: {
  date: Date | null;
  setDate: (date: Date | null) => void;
  onCancel: () => void;
  ref?: React.RefObject<DatePickerRef | null>;
  autoFocus?: boolean;
}) {
  const today = useMemo(() => DateTime.now(), []);

  const tz = useTimeZone();
  if (!props.date) {
    return null;
  }
  switch (Platform.OS) {
    case "android":
      return (
        <Box
          style={{ flexDirection: "row", justifyContent: "space-between" }}
          spaceHorizontal="medium"
        >
          <DPAndroid
            date={props.date}
            setDate={props.setDate}
            onCancel={props.onCancel}
          />
          <ClearButton onPress={() => props.setDate(null)} />
        </Box>
      );
    case "ios":
      return (
        <Box
          spaceHorizontal="medium"
          spaceLeft="small"
          spaceVertical="small"
          style={{ flexDirection: "row", justifyContent: "space-between" }}
        >
          <DateTimePicker
            themeVariant="light"
            value={props.date ?? undefined}
            mode="datetime"
            display="compact"
            minimumDate={new Date()}
            locale={getActiveSystemLocale()}
            onChange={(_, selectedDate) => {
              if (selectedDate) {
                props.setDate(selectedDate);
              }
            }}
          />

          <ClearButton onPress={() => props.setDate(null)} />
        </Box>
      );
    case "web":
      return (
        <Box
          spaceHorizontal="medium"
          spaceVertical="small"
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <WebDateSelector
            autoFocus={props.autoFocus}
            ref={props.ref}
            type="datetime"
            timezone={tz}
            minDate={today}
            value={props.date ? DateTime.fromJSDate(props.date) : null}
            onChangeValue={(selectedDate) => {
              if (selectedDate) {
                const date = selectedDate.toJSDate();
                props.setDate(date);
              } else {
                props.setDate(null);
              }
            }}
          />
          <ClearButton onPress={() => props.setDate(null)} />
        </Box>
      );
    default:
      return null;
  }
}
export function TodoEditDeadlinePopover(props: {
  todo: TodoEditDeadlinePopover_todo$key;
  visible: boolean;
  onClose: () => void;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoEditDeadlinePopover_todo on Todo {
        id
        expireAt
      }
    `,
    props.todo
  );

  const [expireAt, setExpireAt] = useTodoEditSetExpireAt(
    todo.id,
    todo.expireAt ?? undefined
  );

  return (
    <TodoEditDeadlinePopoverComponent
      visible={props.visible}
      expireAt={expireAt ?? null}
      setExpireAt={(date) => setExpireAt(date ?? undefined)}
      onClose={props.onClose}
    />
  );
}
